import React, { useCallback, useEffect, useRef, useState } from 'react';

const DEFAULT_INPUT_CLASS =
  'w-full px-2 py-1 rounded bg-black border border-gray-700 text-xs md:text-sm text-white focus:outline-none focus:ring-1 focus:ring-gray-600';

/**
 * Type name and draw signature at the same time. Optional date sits on the
 * same row as the text input (right side).
 */
export function HandwrittenSignatureField({
  textValue = '',
  imageValue = '',
  mode: _mode,
  onModeChange: _onModeChange,
  onTextChange,
  onImageChange,
  placeholder = 'Type name / company',
  inputClassName = '',
  dateValue,
  onDateChange,
}) {
  const fieldClass = inputClassName || DEFAULT_INPUT_CLASS;
  const showDate = typeof onDateChange === 'function';

  return (
    <div className="space-y-2 signature-field-tabs">
      <div className="flex items-center gap-2">
        <div className="min-w-0 flex-1">
          <input
            type="text"
            value={textValue}
            onChange={(e) => onTextChange?.(e.target.value)}
            placeholder={placeholder}
            className={fieldClass}
          />
        </div>
        {showDate ? (
          <div className="w-[10.5rem] shrink-0">
            <input
              type="date"
              value={dateValue || ''}
              onChange={(e) => onDateChange(e.target.value)}
              className={fieldClass}
              aria-label="Date"
            />
          </div>
        ) : null}
      </div>
      <SignatureDrawPad value={imageValue} onChange={onImageChange} />
    </div>
  );
}

export function SignatureDrawPad({ value = '', onChange }) {
  const canvasRef = useRef(null);
  const drawingRef = useRef(false);
  const lastPointRef = useRef(null);
  const hasInkRef = useRef(Boolean(value));
  const [isEmpty, setIsEmpty] = useState(!value);

  const getPoint = useCallback((event, canvas) => {
    const rect = canvas.getBoundingClientRect();
    const source = event.touches?.[0] || event;
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    return {
      x: (source.clientX - rect.left) * scaleX,
      y: (source.clientY - rect.top) * scaleY,
    };
  }, []);

  const paintBackground = useCallback((ctx, canvas) => {
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
  }, []);

  const redrawFromValue = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    paintBackground(ctx, canvas);
    if (!value) {
      hasInkRef.current = false;
      setIsEmpty(true);
      return;
    }
    const img = new Image();
    img.onload = () => {
      ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
      hasInkRef.current = true;
      setIsEmpty(false);
    };
    img.src = value;
  }, [paintBackground, value]);

  useEffect(() => {
    if (drawingRef.current) return;
    redrawFromValue();
  }, [redrawFromValue]);

  const persistCanvas = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas || !hasInkRef.current) return;
    onChange?.(canvas.toDataURL('image/png'));
  }, [onChange]);

  const startDraw = (event) => {
    event.preventDefault();
    const canvas = canvasRef.current;
    if (!canvas) return;
    drawingRef.current = true;
    lastPointRef.current = getPoint(event, canvas);
  };

  const moveDraw = (event) => {
    if (!drawingRef.current) return;
    event.preventDefault();
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    const point = getPoint(event, canvas);
    const last = lastPointRef.current;
    ctx.strokeStyle = '#111827';
    ctx.lineWidth = 2.2;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.beginPath();
    if (last) ctx.moveTo(last.x, last.y);
    else ctx.moveTo(point.x, point.y);
    ctx.lineTo(point.x, point.y);
    ctx.stroke();
    lastPointRef.current = point;
    hasInkRef.current = true;
    setIsEmpty(false);
  };

  const endDraw = (event) => {
    if (!drawingRef.current) return;
    event.preventDefault();
    drawingRef.current = false;
    lastPointRef.current = null;
    persistCanvas();
  };

  const clearPad = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    paintBackground(ctx, canvas);
    hasInkRef.current = false;
    setIsEmpty(true);
    onChange?.('');
  };

  return (
    <div className="space-y-1">
      <div className="relative rounded border border-gray-600 bg-white overflow-hidden">
        <canvas
          ref={canvasRef}
          width={480}
          height={160}
          className="block w-full h-[96px] touch-none cursor-crosshair"
          onMouseDown={startDraw}
          onMouseMove={moveDraw}
          onMouseUp={endDraw}
          onMouseLeave={endDraw}
          onTouchStart={startDraw}
          onTouchMove={moveDraw}
          onTouchEnd={endDraw}
          aria-label="Draw signature"
        />
        {isEmpty && (
          <span className="pointer-events-none absolute inset-0 flex items-center justify-center text-sm text-gray-400 opacity-30">
            Draw signature here
          </span>
        )}
      </div>
      <div className="flex flex-wrap items-center justify-between gap-2">
        {value ? <p className="text-[10px] text-gray-500">Saved automatically</p> : <span />}
        <button
          type="button"
          onClick={clearPad}
          className="signature-pad-clear text-[11px] text-amber-200/80 hover:text-amber-200 underline"
        >
          Clear
        </button>
      </div>
      {value ? (
        <img src={value} alt="" className="sr-only signature-draw-capture" aria-hidden="true" />
      ) : null}
    </div>
  );
}

function resizeSignatureImage(dataUrl, maxW = 480, maxH = 160) {
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => {
      const canvas = document.createElement('canvas');
      let w = img.width;
      let h = img.height;
      const scale = Math.min(maxW / w, maxH / h, 1);
      w = Math.max(1, Math.round(w * scale));
      h = Math.max(1, Math.round(h * scale));
      canvas.width = maxW;
      canvas.height = maxH;
      const ctx = canvas.getContext('2d');
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, maxW, maxH);
      const x = Math.round((maxW - w) / 2);
      const y = Math.round((maxH - h) / 2);
      ctx.drawImage(img, x, y, w, h);
      resolve(canvas.toDataURL('image/png'));
    };
    img.onerror = () => resolve(dataUrl);
    img.src = dataUrl;
  });
}

const PROFILE_SIG_MODES = [
  { id: 'type', label: 'Type' },
  { id: 'draw', label: 'Draw' },
  { id: 'upload', label: 'Upload' },
];

/**
 * Configure-profile signature: type, draw on pad, or upload an image.
 * Value is plain text or a data:image URL.
 */
export function ProfileSignatureEditor({
  value = '',
  onChange,
  disabled = false,
  inputClassName = '',
}) {
  const isImage = typeof value === 'string' && value.startsWith('data:image');
  const [mode, setMode] = useState(() => (isImage ? 'draw' : 'type'));
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (isImage) {
      setMode((prev) => (prev === 'type' ? 'draw' : prev));
    }
  }, [isImage]);

  const switchMode = (next) => {
    setMode(next);
    if (next === 'type' && isImage) onChange?.('');
    if ((next === 'draw' || next === 'upload') && value && !isImage) onChange?.('');
  };

  const handleUpload = (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file || !file.type.startsWith('image/')) return;
    const reader = new FileReader();
    reader.onload = async () => {
      const raw = typeof reader.result === 'string' ? reader.result : '';
      if (!raw) return;
      const resized = await resizeSignatureImage(raw);
      onChange?.(resized);
    };
    reader.readAsDataURL(file);
  };

  return (
    <div className="space-y-3">
      <div className="inline-flex rounded-lg border border-gray-700 bg-black p-0.5">
        {PROFILE_SIG_MODES.map((item) => {
          const active = mode === item.id;
          return (
            <button
              key={item.id}
              type="button"
              disabled={disabled}
              onClick={() => switchMode(item.id)}
              className={`px-3 py-1.5 text-xs font-semibold rounded-md transition-colors disabled:opacity-40 ${
                active
                  ? 'bg-[#eda448] text-gray-900'
                  : 'text-gray-400 hover:text-white'
              }`}
            >
              {item.label}
            </button>
          );
        })}
      </div>

      {mode === 'type' ? (
        <input
          id="profile-signature"
          name="signature"
          type="text"
          value={isImage ? '' : value}
          onChange={(e) => onChange?.(e.target.value)}
          disabled={disabled}
          required={!isImage}
          className={inputClassName}
          placeholder="Type your signature"
        />
      ) : null}

      {mode === 'draw' ? (
        <div className={disabled ? 'pointer-events-none opacity-50' : ''}>
          <SignatureDrawPad value={isImage ? value : ''} onChange={onChange} />
        </div>
      ) : null}

      {mode === 'upload' ? (
        <div className="space-y-2">
          <input
            ref={fileInputRef}
            type="file"
            accept="image/png,image/jpeg,image/webp,image/gif"
            className="hidden"
            disabled={disabled}
            onChange={handleUpload}
          />
          <button
            type="button"
            disabled={disabled}
            onClick={() => fileInputRef.current?.click()}
            className="w-full py-3 rounded-lg border border-dashed border-gray-600 bg-black text-sm text-gray-300 hover:border-amber-300/50 hover:text-amber-100 transition-colors disabled:opacity-40"
          >
            {isImage ? 'Replace handwritten signature image' : 'Upload handwritten signature image'}
          </button>
          {isImage ? (
            <div className="flex items-center justify-between gap-2">
              <p className="text-[11px] text-gray-500">Image ready to save</p>
              <button
                type="button"
                disabled={disabled}
                onClick={() => onChange?.('')}
                className="text-[11px] text-amber-200/80 hover:text-amber-200 underline disabled:opacity-40"
              >
                Clear
              </button>
            </div>
          ) : (
            <p className="text-[11px] text-gray-500">PNG, JPG, or WebP. Saved with your profile.</p>
          )}
        </div>
      ) : null}
    </div>
  );
}

